export const M1_UNIT_10 = {
  id: "u10-talking-job",
  title: "Unidad 10: Talking About Your Job",
  topic: "Jobs, Workplaces & Prepositions",
  iconType: "briefcase",
  grammarImage: "/u10-job-description.jpg",
  grammarText: "Uso de 'a/an' + Job. Preposiciones: Work IN (lugares cerrados), ON (superficies/granjas), WITH (personas/objetos).",
  sets: [
    // ----------------------------------------------------------------------
    // SET 1: BASIC IDENTITY (I am a...)
    // Enfoque: Identificar la profesión por la acción.
    // ----------------------------------------------------------------------
    {
      id: "u10-set1",
      title: "Set 1: What do you do?",
      questions: [
        {
          type: "match",
          q: "Match the job to the action",
          pairs: [
            { left: "Doctor", right: "Helps sick people" },
            { left: "Teacher", right: "Teaches students" },
            { left: "Chef", right: "Cooks food" }
          ],
          a: "completed"
        },
        { type: "mc", q: "I work in a school. I am a ___.", options: ["teacher", "doctor", "pilot"], a: "teacher" },
        { type: "mc", q: "I fly airplanes to different countries. I am a ___.", options: ["Pilot", "Driver", "Vet"], a: "Pilot" },
        { type: "input", q: "I wear a white coat and work in a hospital. I am a ___.", a: "doctor" },
        { type: "order", q: "Ordena", words: ["I", "am", "a", "teacher."], a: "I am a teacher." },
        { type: "match",
          q: "Match professional to object",
          pairs: [
            { left: "Pilot", right: "Plane" },
            { left: "Driver", right: "Bus" },
            { left: "Chef", right: "Kitchen" }
          ],
          a: "completed"
        },
        { type: "mc", q: "She assists the doctor in the hospital.", options: ["Nurse", "Teacher", "Artist"], a: "Nurse" },
        { type: "input", q: "I drive a taxi all day. I am a ___.", a: "driver" },
        { type: "input", q: "I make delicious food. I am a ___.", a: "chef" },
        { type: "order", q: "Ordena", words: ["She", "is", "a", "doctor."], a: "She is a doctor." }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 2: SERVICE & CITY JOBS (Vocabulario Urbano)
    // Enfoque: Trabajos que se ven en la ciudad.
    // ----------------------------------------------------------------------
    {
      id: "u10-set2",
      title: "Set 2: City Services",
      questions: [
        {
          type: "match",
          q: "Where do they work?",
          pairs: [
            { left: "Waiter", right: "Restaurant" },
            { left: "Shop Assistant", right: "Store" },
            { left: "Hairdresser", right: "Salon" }
          ],
          a: "completed"
        },
        { type: "mc", q: "Can I bring you the menu? I am a ___.", options: ["waiter", "police officer", "vet"], a: "waiter" },
        { type: "mc", q: "I protect the city and stop crime.", options: ["Police Officer", "Teacher", "Actor"], a: "Police Officer" },
        { type: "input", q: "I wash, cut, and style hair. I am a ___.", a: "hairdresser" },
        { type: "match",
          q: "Singular vs Plural",
          pairs: [
            { left: "One Policeman", right: "Two Policemen" },
            { left: "One Woman", right: "Two Women" },
            { left: "One Person", right: "Two People" }
          ],
          a: "completed"
        },
        { type: "input", q: "I serve coffee and food. I am a ___.", a: "waiter" },
        { type: "order", q: "Ordena", words: ["He", "is", "a", "waiter."], a: "He is a waiter." },
        { type: "mc", q: "I answer phones at the hotel entrance.", options: ["Receptionist", "Cleaner", "Driver"], a: "Receptionist" },
        { type: "input", q: "I clean offices and schools. I am a ___.", a: "cleaner" },
        { type: "order", q: "Ordena", words: ["They", "are", "police", "officers."], a: "They are police officers." }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 3: SKILLED TRADES & STEM (Vocabulario Técnico)
    // Enfoque: "I fix..." / "I build..."
    // ----------------------------------------------------------------------
    {
      id: "u10-set3",
      title: "Set 3: Skilled Jobs",
      questions: [
        {
          type: "match",
          q: "Who fixes what?",
          pairs: [
            { left: "Mechanic", right: "Cars" },
            { left: "Dentist", right: "Teeth" },
            { left: "Vet", right: "Animals" }
          ],
          a: "completed"
        },
        { type: "mc", q: "I design bridges and machines.", options: ["Engineer", "Artist", "Farmer"], a: "Engineer" },
        { type: "mc", q: "I do experiments in a laboratory.", options: ["Scientist", "Actor", "Driver"], a: "Scientist" },
        { type: "input", q: "My car is broken. I need a ___.", a: "mechanic" },
        { type: "order", q: "Ordena", words: ["I", "am", "an", "engineer."], a: "I am an engineer." },
        { type: "match",
          q: "Match job to action",
          pairs: [
            { left: "Firefighter", right: "Fights fires" },
            { left: "Artist", right: "Paints pictures" },
            { left: "Actor", right: "Makes movies" }
          ],
          a: "completed"
        },
        { type: "input", q: "My dog is sick. I need a ___.", a: "vet" },
        { type: "mc", q: "I build houses and walls.", options: ["Construction Worker", "Doctor", "Vet"], a: "Construction Worker" },
        { type: "input", q: "I act in movies and theater. I am an ___.", a: "actor" },
        { type: "order", q: "Ordena", words: ["We", "are", "scientists."], a: "We are scientists." }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 4: WORKPLACES (Lugares)
    // Enfoque: Preposición IN implícita (lugares cerrados).
    // ----------------------------------------------------------------------
    {
      id: "u10-set4",
      title: "Set 4: Workplaces",
      questions: [
        {
          type: "match",
          q: "Where do they work?",
          pairs: [
            { left: "Teacher", right: "School" },
            { left: "Doctor", right: "Hospital" },
            { left: "Farmer", right: "Farm" }
          ],
          a: "completed"
        },
        { type: "mc", q: "A waiter works in a ___.", options: ["Restaurant", "Police Station", "Bank"], a: "Restaurant" },
        { type: "mc", q: "A scientist works in a ___.", options: ["Laboratory", "Theater", "Shop"], a: "Laboratory" },
        { type: "input", q: "An actor works in a ___.", a: "theater" },
        { type: "match",
          q: "Indoor vs Outdoor",
          pairs: [
            { left: "Farmer", right: "Outside" },
            { left: "Receptionist", right: "Inside" },
            { left: "Driver", right: "In a vehicle" }
          ],
          a: "completed"
        },
        { type: "input", q: "A police officer works in a police ___.", a: "station" },
        { type: "order", q: "Ordena", words: ["She", "works", "in", "a", "bank."], a: "She works in a bank." },
        { type: "mc", q: "A secretary works in an ___.", options: ["Office", "Farm", "Street"], a: "Office" },
        { type: "input", q: "I work outside with cows and sheep. It is a ___.", a: "farm" },
        { type: "order", q: "Ordena", words: ["He", "works", "in", "an", "office."], a: "He works in an office." }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 5: A / AN (Artículos)
    // Enfoque: Sonido (Vocal vs Consonante) sin reglas escritas.
    // ----------------------------------------------------------------------
    {
      id: "u10-set5",
      title: "Set 5: A or An?",
      questions: [
        {
          type: "match",
          q: "Match the Article",
          pairs: [
            { left: "A", right: "Doctor" },
            { left: "An", right: "Engineer" },
            { left: "A", right: "Teacher" }
          ],
          a: "completed"
        },
        { type: "mc", q: "She is ___ architect.", options: ["an", "a", "two"], a: "an" },
        { type: "mc", q: "He is ___ police officer.", options: ["a", "an", "one"], a: "a" },
        { type: "input", q: "I am ___ (a/an) artist.", a: "an" },
        { type: "match",
          q: "Sort by sound",
          pairs: [
            { left: "A", right: "Nurse" },
            { left: "An", right: "Apple" },
            { left: "An", right: "Office" }
          ],
          a: "completed"
        },
        { type: "input", q: "You are ___ (a/an) good student.", a: "a" },
        { type: "order", q: "Ordena", words: ["He", "is", "an", "actor."], a: "He is an actor." },
        { type: "mc", q: "My father is ___ electrician.", options: ["an", "a", "the"], a: "an" },
        { type: "input", q: "Is she ___ (a/an) vet?", a: "a" },
        { type: "order", q: "Ordena", words: ["I", "am", "an", "English", "student."], a: "I am an English student." }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 6: PREPOSITIONS (In, On, With)
    // Enfoque: Lógica espacial y social.
    // ----------------------------------------------------------------------
    {
      id: "u10-set6",
      title: "Set 6: In, On, With",
      questions: [
        {
          type: "match",
          q: "Match the preposition",
          pairs: [
            { left: "Work IN", right: "A hospital (Building)" },
            { left: "Work ON", right: "A farm (Surface)" },
            { left: "Work WITH", right: "Children (People)" }
          ],
          a: "completed"
        },
        { type: "mc", q: "A vet works ___ animals.", options: ["with", "on", "in"], a: "with" },
        { type: "mc", q: "A construction worker works ___ a building site.", options: ["on", "in", "with"], a: "on" },
        { type: "input", q: "I am a teacher. I work ___ (in/on) a school.", a: "in" },
        { type: "match",
          q: "Connect the phrases",
          pairs: [
            { left: "Inside", right: "In an office" },
            { left: "Outside", right: "On a site" },
            { left: "Together", right: "With people" }
          ],
          a: "completed"
        },
        { type: "input", q: "I am a gardener. I work ___ (with/in) plants.", a: "with" },
        { type: "order", q: "Ordena", words: ["I", "work", "on", "a", "farm."], a: "I work on a farm." },
        { type: "mc", q: "Doctors work ___ patients.", options: ["with", "at", "on"], a: "with" },
        { type: "input", q: "She is a chef. She works ___ (with/on) food.", a: "with" },
        { type: "order", q: "Ordena", words: ["He", "works", "with", "computers."], a: "He works with computers." }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 7: PLURALS (Gramática de Sujeto)
    // Enfoque: I am vs We are vs They are.
    // ----------------------------------------------------------------------
    {
      id: "u10-set7",
      title: "Set 7: Singular & Plural",
      questions: [
        {
          type: "match",
          q: "Singular vs Plural",
          pairs: [
            { left: "I am", right: "A doctor" },
            { left: "We are", right: "Doctors" },
            { left: "They are", right: "Teachers" }
          ],
          a: "completed"
        },
        { type: "mc", q: "She ___ a pilot.", options: ["is", "are", "am"], a: "is" },
        { type: "mc", q: "They ___ engineers.", options: ["are", "is", "am"], a: "are" },
        { type: "input", q: "We ___ (verb) police officers.", a: "are" },
        { type: "match",
          q: "Form the sentence",
          pairs: [
            { left: "She is", right: "a nurse" },
            { left: "They are", right: "nurses" },
            { left: "You are", right: "a nurse" }
          ],
          a: "completed"
        },
        { type: "input", q: "He ___ (verb) not a waiter.", a: "is" },
        { type: "order", q: "Ordena", words: ["We", "are", "not", "students."], a: "We are not students." },
        { type: "mc", q: "___ they architects?", options: ["Are", "Is", "Am"], a: "Are" },
        { type: "input", q: "They work in a restaurant. They are ___ (waiter plural).", a: "waiters" },
        { type: "order", q: "Ordena", words: ["My", "brothers", "are", "mechanics."], a: "My brothers are mechanics." }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 8: GUESS THE JOB (Adivinanzas)
    // Enfoque: Comprensión lectora.
    // ----------------------------------------------------------------------
    {
      id: "u10-set8",
      title: "Set 8: Guess the Job",
      questions: [
        { type: "mc", q: "'I drive a bus.' Who am I?", options: ["Driver", "Pilot", "Mechanic"], a: "Driver" },
        { type: "mc", q: "'I grow vegetables outside.' Who am I?", options: ["Farmer", "Chef", "Grocer"], a: "Farmer" },
        { type: "match",
          q: "Match description to job",
          pairs: [
            { left: "I serve food", right: "Waiter" },
            { left: "I cook food", right: "Chef" },
            { left: "I sell food", right: "Sales Assistant" }
          ],
          a: "completed"
        },
        { type: "input", q: "'I fight fires and drive a big red truck.' I am a ___.", a: "firefighter" },
        { type: "input", q: "'I help you when you are sick.' I am a ___.", a: "doctor" },
        { type: "order", q: "Ordena", words: ["What", "do", "you", "do?"], a: "What do you do?" },
        { type: "mc", q: "'I paint pictures and make art.'", options: ["Artist", "Actor", "Architect"], a: "Artist" },
        { type: "match",
          q: "Who uses this?",
          pairs: [
            { left: "Whiteboard", right: "Teacher" },
            { left: "Stethoscope", right: "Doctor" },
            { left: "Tractor", right: "Farmer" }
          ],
          a: "completed"
        },
        { type: "input", q: "'I check your teeth.' I am a ___.", a: "dentist" },
        { type: "order", q: "Ordena", words: ["I", "am", "a", "student."], a: "I am a student." }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 9: MIXED REVIEW (Integración)
    // Enfoque: Cambiar entre vocabulario, preposiciones y gramática.
    // ----------------------------------------------------------------------
    {
      id: "u10-set9",
      title: "Set 9: Mixed Review",
      questions: [
        { type: "mc", q: "My sister ___ in an office.", options: ["works", "work", "working"], a: "works" },
        { type: "mc", q: "Does he ___ with children?", options: ["work", "works", "worker"], a: "work" },
        { type: "input", q: "A ___ (job) catches criminals.", a: "police officer" },
        { type: "match",
          q: "Complete the sentence",
          pairs: [
            { left: "I am an", right: "Engineer" },
            { left: "I am a", right: "Doctor" },
            { left: "We are", right: "Doctors" }
          ],
          a: "completed"
        },
        { type: "order", q: "Ordena", words: ["Where", "do", "you", "work?"], a: "Where do you work?" },
        { type: "input", q: "I don't work ___ (preposition) a farm.", a: "on" },
        { type: "mc", q: "Select the odd one out (Different)", options: ["Hospital", "School", "Teacher"], a: "Teacher" },
        { type: "input", q: "My father is a businessman. He works in an ___.", a: "office" },
        { type: "order", q: "Ordena", words: ["She", "works", "with", "animals."], a: "She works with animals." },
        { type: "mc", q: "Plural of 'Businesswoman'", options: ["Businesswomen", "Businesswomans"], a: "Businesswomen" }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 10: MASTERY (Reto Final)
    // Enfoque: Nivel A2 - Oraciones compuestas y vocabulario extra.
    // ----------------------------------------------------------------------
    {
      id: "u10-set10",
      title: "Set 10: Final Challenge",
      questions: [
        { type: "order", q: "Complex Sentence", words: ["I", "am", "a", "nurse", "and", "I", "work", "in", "a", "hospital."], a: "I am a nurse and I work in a hospital." },
        { type: "input", q: "Complete: 'I design buildings.' I am an ___.", a: "architect" },
        { type: "match",
          q: "Ultimate Job Match",
          pairs: [
            { left: "Journalist", right: "Newspaper" },
            { left: "Pilot", right: "Airport" },
            { left: "Scientist", right: "Laboratory" },
            { left: "Actor", right: "Theater" }
          ],
          a: "completed"
        },
        { type: "mc", q: "He works ___ night.", options: ["at", "on", "in"], a: "at" },
        { type: "input", q: "Plural of 'Secretary': Two ___.", a: "secretaries" },
        { type: "order", q: "Ordena", words: ["Do", "you", "work", "in", "an", "office?"], a: "Do you work in an office?" },
        { type: "mc", q: "Which is correct?", options: ["an university", "a university"], a: "a university" },
        { type: "input", q: "Complete: 'I play music.' I am a ___.", a: "musician" },
        { type: "match",
          q: "Opposites",
          pairs: [
            { left: "Work", right: "Rest" },
            { left: "Job", right: "Hobby" },
            { left: "Inside", right: "Outside" }
          ],
          a: "completed"
        },
        { type: "order", q: "Final", words: ["I", "love", "my", "job!"], a: "I love my job!" }
      ]
    }
  ]
};